import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/store';

/** One lock the current session holds. Mirrors the BE `HeldLock` record. */
export interface HeldLock {
  /** `location` | `pallet` | `carton` | `content` | `station` | `order` — machine values. */
  kind: string;
  warehouseId: string;
  id: string;
  /** Extra identity where the kind needs it (an order lock's release number). */
  detail?: string | null;
}

export interface HeldLocksResponse {
  count: number;
  items: HeldLock[];
}

export const MY_LOCKS_KEY = ['locks', 'mine'] as const;

/** Poll cadence. Locks change on operator actions, not continuously — a minute is plenty. */
export const MY_LOCKS_POLL_MS = 60_000;

/**
 * The current session's held inventory locks (BigList #18).
 *
 * <p>Polled rather than pushed, and polled from the shell rather than from the six screens that
 * take locks, because the operator most needs to know AFTER they have navigated away from the
 * screen that took the lock — that is precisely the case PB leaves them with no signal at all
 * (`docs/audits/biglist/18-inventory-locking.md` §1.4: the only cure in PB today is an
 * administrator opening Lock Maintenance and deleting rows by hand).
 *
 * <p>Disabled without a session so it does not fire on `/login`, and `retry: false` so a BE
 * hiccup does not turn a background poll into a burst.
 */
export function useMyLocks() {
  const session = useAuthStore((s) => s.session);
  return useQuery<HeldLocksResponse>({
    queryKey: MY_LOCKS_KEY,
    queryFn: () => api<HeldLocksResponse>('/api/locks/mine'),
    enabled: session != null,
    refetchInterval: MY_LOCKS_POLL_MS,
    // The banner is advisory; a stale count for one poll cycle is fine, a retry storm is not.
    retry: false,
    staleTime: MY_LOCKS_POLL_MS,
  });
}

/** "Release all" — releases every lock this session holds. The session itself survives. */
export function useReleaseMyLocks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<HeldLocksResponse>('/api/locks/mine/release', { method: 'POST' }),
    onSuccess: (data) => {
      // The BE returns the post-release state, so seed it rather than waiting for a refetch.
      qc.setQueryData(MY_LOCKS_KEY, data);
    },
  });
}

/**
 * Native "are you sure you want to leave?" prompt while this session holds locks.
 *
 * <p><b>Two honest limits, both deliberate.</b>
 *
 * <p>First, browsers ignore our message. Chrome/Firefox/Safari all show their own generic "Leave
 * site?" copy, so we cannot say "you hold 3 locked locations" in the native dialog — the same
 * constraint {@link useUnsavedChangesGuard} documents. That is why the informative half is the
 * in-page banner and this is only the backstop.
 *
 * <p>Second, this WARNS; it does not release. `beforeunload` cannot reliably release: slice-336
 * removed a `pagehide` listener from `useLogoutOnClose` because browsers fire it on F5/Ctrl+R too
 * and operators were being dumped on `/login` mid-form. The sweeper (ADR-0044) is what cleans up
 * a session that really did go away. Deliberately no `sendBeacon` either — a beacon that fires on
 * refresh would release the locks of a session that is about to carry straight on using them.
 */
export function useHeldLocksGuard(heldCount: number): void {
  useEffect(() => {
    if (heldCount <= 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [heldCount]);
}
